/**
 * FeedbackModal Component
 *
 * Modal dialog for submitting feedback (bugs, enhancements, questions).
 * Features tabbed forms with smart environment auto-capture.
 */

import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Bug, Lightbulb, Loader2, Monitor, Upload, X, ChevronDown, ChevronUp, Terminal } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';
import { captureEnvironment, getEnvironmentSummary } from '@/utils/feedbackEnvironment';
import { getCapturedLogsAsString, getLogBufferSize } from '@/utils/consoleLogBuffer';
import { createFeedback } from '@/services/feedbackService';
import { supabase } from '@/utils/supabase';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  bugReportFormSchema,
  enhancementFormSchema,
  defaultBugReportValues,
  defaultEnhancementValues,
  type BugReportFormValues,
  type EnhancementFormValues
} from './feedback-validations';
import {
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_SEVERITY_LABELS,
  type FeedbackCategory,
  type FeedbackSeverity,
  type FeedbackType
} from '@/types/feedback';

const log = createComponentLogger('FeedbackModal', 'feedback');

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select a specific tab */
  defaultTab?: FeedbackType;
}

interface UploadedFile {
  name: string;
  /** Storage path (persisted; private bucket — signed URLs generated on read). */
  path: string;
  type: string;
  size: number;
  /** Local-only blob URL for in-modal preview. Revoked on close; never persisted. */
  previewUrl: string;
}

export function FeedbackModal({
  open,
  onOpenChange,
  defaultTab = 'enhancement'
}: FeedbackModalProps) {
  const [activeTab, setActiveTab] = useState<FeedbackType>(defaultTab);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenshots, setScreenshots] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showConsoleLogs, setShowConsoleLogs] = useState(false);
  const [includeConsoleLogs, setIncludeConsoleLogs] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const enhancementFileInputRef = useRef<HTMLInputElement>(null);
  const environmentSummary = getEnvironmentSummary();
  const consoleLogs = getCapturedLogsAsString();
  const logCount = getLogBufferSize();

  // Bug report form
  const bugForm = useForm<BugReportFormValues>({
    resolver: zodResolver(bugReportFormSchema),
    defaultValues: defaultBugReportValues
  });

  // Enhancement form
  const enhancementForm = useForm<EnhancementFormValues>({
    resolver: zodResolver(enhancementFormSchema),
    defaultValues: defaultEnhancementValues
  });


  // Allowed file types
  const ALLOWED_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];

  // Handle file upload
  const handleFileUpload = async (files: FileList | null, imagesOnly = false) => {
    if (!files || files.length === 0) {
return;
}

    setIsUploading(true);
    const newScreenshots: UploadedFile[] = [];

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be signed in to upload files');
        return;
      }

      for (const file of Array.from(files)) {
        // Validate file type
        if (imagesOnly && !file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }
        if (!imagesOnly && !ALLOWED_TYPES.includes(file.type)) {
          toast.error(`${file.name} has an unsupported file type`);
          continue;
        }

        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }

        // Upload to private bucket under the user's uid prefix (matches RLS).
        const sanitized = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const objectPath = `${user.id}/${Date.now()}-${sanitized}`;
        const { data, error } = await supabase.storage
          .from('feedback-attachments')
          .upload(objectPath, file);

        if (error) {
          log.error('Failed to upload file', { objectPath }, error);
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }

        // Local blob URL for preview only — never persisted.
        const previewUrl = URL.createObjectURL(file);

        newScreenshots.push({
          name: file.name,
          path: data.path,
          type: file.type,
          size: file.size,
          previewUrl
        });
      }

      setScreenshots((prev) => [...prev, ...newScreenshots]);
      if (newScreenshots.length > 0) {
        toast.success(`${newScreenshots.length} file(s) uploaded`);
      }
    } catch (error) {
      log.error('File upload error', {}, error as Error);
      toast.error('Failed to upload files');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (enhancementFileInputRef.current) {
        enhancementFileInputRef.current.value = '';
      }
    }
  };

  // Remove uploaded screenshot
  const removeScreenshot = (index: number) => {
    setScreenshots((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // Reset form state when modal closes (revoke any in-flight preview blobs).
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      screenshots.forEach((s) => {
        if (s.previewUrl) {
          URL.revokeObjectURL(s.previewUrl);
        }
      });
      setScreenshots([]);
      setShowConsoleLogs(false);
      bugForm.reset();
      enhancementForm.reset();
    }
    onOpenChange(newOpen);
  };

  /** Strip the local-only preview URL before persisting attachments. */
  const toPersistedAttachments = (uploads: UploadedFile[]) =>
    uploads.map(({ name, path, type, size }) => ({ name, path, type, size }));

  const handleSubmitBug = async (data: BugReportFormValues) => {
    setIsSubmitting(true);
    try {
      const environment = captureEnvironment();

      // Build description with console logs if included
      let fullDescription = data.description;
      if (includeConsoleLogs && logCount > 0) {
        fullDescription += `\n\n---\n**Console Logs (${logCount} entries):**\n\`\`\`\n${consoleLogs}\n\`\`\``;
      }

      const result = await createFeedback({
        title: data.title,
        type: 'bug',
        description: fullDescription,
        category: data.category,
        severity: data.severity,
        steps_to_reproduce: data.steps_to_reproduce || undefined,
        expected_behavior: data.expected_behavior || undefined,
        actual_behavior: data.actual_behavior || undefined,
        attachments: screenshots.length > 0 ? toPersistedAttachments(screenshots) : undefined
      }, environment);

      log.info('Bug report submitted', { ticketId: result.ticket_id, attachments: screenshots.length });
      toast.success(`Bug report submitted! Ticket ID: ${result.ticket_id}`);
      bugForm.reset();
      setScreenshots([]);
      handleOpenChange(false);
    } catch (error) {
      log.error('Failed to submit bug report', {}, error as Error);
      toast.error('Failed to submit bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEnhancement = async (data: EnhancementFormValues) => {
    setIsSubmitting(true);
    try {
      const environment = captureEnvironment();
      const result = await createFeedback({
        title: data.title,
        type: 'enhancement',
        description: data.description + (data.use_case ? `\n\n**Use Case:**\n${data.use_case}` : ''),
        category: data.category,
        attachments: screenshots.length > 0 ? toPersistedAttachments(screenshots) : undefined
      }, environment);

      log.info('Enhancement request submitted', { ticketId: result.ticket_id });
      toast.success(`Enhancement request submitted! Ticket ID: ${result.ticket_id}`);
      enhancementForm.reset();
      setScreenshots([]);
      handleOpenChange(false);
    } catch (error) {
      log.error('Failed to submit enhancement request', {}, error as Error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  const categories = Object.entries(FEEDBACK_CATEGORY_LABELS) as [FeedbackCategory, string][];
  const severities = Object.entries(FEEDBACK_SEVERITY_LABELS) as [FeedbackSeverity, string][];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Submit Feedback</DialogTitle>
          <DialogDescription>
            Help us improve Lendwise by reporting bugs, suggesting features, or asking questions.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as FeedbackType)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="enhancement" className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4" />
              Enhancement
            </TabsTrigger>
            <TabsTrigger value="bug" className="flex items-center gap-2">
              <Bug className="h-4 w-4" />
              Bug
            </TabsTrigger>
          </TabsList>

          {/* Bug Report Form */}
          <TabsContent value="bug" className="mt-4">
            <form onSubmit={bugForm.handleSubmit(handleSubmitBug)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bug-title">Title *</Label>
                <Input
                  id="bug-title"
                  placeholder="Brief description of the bug"
                  {...bugForm.register('title')}
                />
                {bugForm.formState.errors.title && (
                  <p className="text-sm text-destructive">{bugForm.formState.errors.title.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bug-category">Category *</Label>
                  <Select
                    value={bugForm.watch('category')}
                    onValueChange={(v) => bugForm.setValue('category', v as FeedbackCategory)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bug-severity">Severity *</Label>
                  <Select
                    value={bugForm.watch('severity')}
                    onValueChange={(v) => bugForm.setValue('severity', v as FeedbackSeverity)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select severity" />
                    </SelectTrigger>
                    <SelectContent>
                      {severities.map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bug-description">Description *</Label>
                <Textarea
                  id="bug-description"
                  placeholder="Describe the bug in detail..."
                  rows={3}
                  {...bugForm.register('description')}
                />
                {bugForm.formState.errors.description && (
                  <p className="text-sm text-destructive">{bugForm.formState.errors.description.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bug-steps">Steps to Reproduce (Optional)</Label>
                <Textarea
                  id="bug-steps"
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                  rows={3}
                  {...bugForm.register('steps_to_reproduce')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bug-expected">Expected Behavior</Label>
                  <Textarea
                    id="bug-expected"
                    placeholder="What should happen?"
                    rows={2}
                    {...bugForm.register('expected_behavior')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bug-actual">Actual Behavior</Label>
                  <Textarea
                    id="bug-actual"
                    placeholder="What actually happened?"
                    rows={2}
                    {...bugForm.register('actual_behavior')}
                  />
                </div>
              </div>

              {/* Screenshot Upload */}
              <div className="space-y-2">
                <Label>Screenshots (Optional)</Label>
                <div
                  className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleFileUpload(e.dataTransfer.files, true);
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files, true)}
                  />
                  {isUploading ? (
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Upload className="h-6 w-6" />
                      <span className="text-sm">Click or drag images here</span>
                      <span className="text-xs">Max 5MB per image</span>
                    </div>
                  )}
                </div>

                {/* Screenshot Previews */}
                {screenshots.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {screenshots.map((file, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={file.previewUrl}
                          alt={file.name}
                          className="h-16 w-16 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => removeScreenshot(index)}
                          className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Console Logs */}
              <Collapsible open={showConsoleLogs} onOpenChange={setShowConsoleLogs}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="include-logs"
                      checked={includeConsoleLogs && logCount > 0}
                      onChange={(e) => setIncludeConsoleLogs(e.target.checked)}
                      disabled={logCount === 0}
                      className="rounded"
                    />
                    <Label htmlFor="include-logs" className={`text-sm cursor-pointer ${logCount === 0 ? 'text-muted-foreground' : ''}`}>
                      {logCount > 0
                        ? `Include console logs (${logCount} entries)`
                        : 'No console warnings/errors captured'}
                    </Label>
                  </div>
                  {logCount > 0 && (
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 px-2">
                        <Terminal className="h-4 w-4 mr-1" />
                        {showConsoleLogs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </CollapsibleTrigger>
                  )}
                </div>
                {logCount > 0 && (
                  <CollapsibleContent>
                    <ScrollArea className="h-32 mt-2 rounded border bg-muted/30">
                      <pre className="p-2 text-xs font-mono whitespace-pre-wrap">{consoleLogs}</pre>
                    </ScrollArea>
                  </CollapsibleContent>
                )}
              </Collapsible>

              {/* Environment Info */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                <Monitor className="h-4 w-4" />
                <span>{environmentSummary}</span>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || isUploading}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Submit Bug Report
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* Enhancement Form */}
          <TabsContent value="enhancement" className="mt-4">
            <form onSubmit={enhancementForm.handleSubmit(handleSubmitEnhancement)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="enhancement-title">Title *</Label>
                <Input
                  id="enhancement-title"
                  placeholder="Brief description of your suggestion"
                  {...enhancementForm.register('title')}
                />
                {enhancementForm.formState.errors.title && (
                  <p className="text-sm text-destructive">{enhancementForm.formState.errors.title.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="enhancement-category">Category *</Label>
                <Select
                  value={enhancementForm.watch('category')}
                  onValueChange={(v) => enhancementForm.setValue('category', v as FeedbackCategory)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="enhancement-description">Description *</Label>
                <Textarea
                  id="enhancement-description"
                  placeholder="Describe your enhancement idea in detail..."
                  rows={4}
                  {...enhancementForm.register('description')}
                />
                {enhancementForm.formState.errors.description && (
                  <p className="text-sm text-destructive">{enhancementForm.formState.errors.description.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="enhancement-usecase">Use Case (Optional)</Label>
                <Textarea
                  id="enhancement-usecase"
                  placeholder="How would this feature help you? What problem does it solve?"
                  rows={3}
                  {...enhancementForm.register('use_case')}
                />
              </div>

              {/* File Upload for Examples */}
              <div className="space-y-2">
                <Label>Attachments (Optional)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Upload screenshots, PDFs, or documents showing examples of what you'd like
                </p>
                <div
                  className="border-2 border-dashed rounded-md p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => enhancementFileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleFileUpload(e.dataTransfer.files, false);
                  }}
                >
                  <input
                    ref={enhancementFileInputRef}
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files, false)}
                  />
                  {isUploading ? (
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Upload className="h-6 w-6" />
                      <span className="text-sm">Click or drag files here</span>
                      <span className="text-xs">Images, PDFs, Word docs (max 10MB)</span>
                    </div>
                  )}
                </div>

                {/* File Previews */}
                {screenshots.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {screenshots.map((file, index) => (
                      <div key={index} className="relative group flex items-center gap-2 bg-muted/50 rounded px-2 py-1">
                        {file.type.startsWith('image/') ? (
                          <img
                            src={file.previewUrl}
                            alt={file.name}
                            className="h-10 w-10 object-cover rounded"
                          />
                        ) : (
                          <div className="h-10 w-10 flex items-center justify-center bg-muted rounded text-xs font-medium">
                            {file.name.split('.').pop()?.toUpperCase()}
                          </div>
                        )}
                        <span className="text-xs truncate max-w-[100px]">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeScreenshot(index)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || isUploading}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Submit Request
                </Button>
              </div>
            </form>
          </TabsContent>

        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
